import React, { useState, useCallback } from 'react';
import { Box, Image } from '@chakra-ui/react';

interface BlurUpImageProps {
  src: string;
  alt: string;
  height?: string | { base?: string; lg?: string };
  width?: string;
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
  borderRadius?: string;
  /**
   * Opt out of the `loading="lazy"` opacity-resolve (variables.css
   * `img[loading="lazy"]`: opacity 0 until the `load` event, then a 250ms
   * fade). Use for an image that must be visible in the very first painted
   * frame, e.g. the profile hero photo, which a view transition snapshots
   * right after mount; the fade would otherwise capture it at opacity 0 and
   * the photo would appear after the slide. Eager images are in the viewport
   * by definition, so skipping lazy loading costs nothing.
   */
  eager?: boolean;
}

/**
 * Photo-container image. Shows the dedicated photo-placeholder token
 * (`--photo-placeholder`) until the image resolves, then the opacity-only
 * fade in variables.css (`img[loading="lazy"]` / `.img-loaded`) reveals it —
 * no per-component load handling needed for the fade itself.
 */
export const BlurUpImage: React.FC<BlurUpImageProps> = ({
  src,
  alt,
  height = '100%',
  width = '100%',
  objectFit = 'cover',
  borderRadius,
  eager = false,
}) => {
  const [hasError, setHasError] = useState(false);

  const handleError = useCallback(() => {
    setHasError(true);
  }, []);

  if (hasError) {
    return (
      <Box
        h={height}
        w={width}
        bg="var(--photo-placeholder)"
        display="flex"
        alignItems="center"
        justifyContent="center"
        borderRadius={borderRadius}
      >
        <Box color="gray.400" fontSize="sm">
          Image unavailable
        </Box>
      </Box>
    );
  }

  return (
    <Box position="relative" h={height} w={width} overflow="hidden" borderRadius={borderRadius} bg="var(--photo-placeholder)">
      <Image
        src={src}
        alt={alt}
        loading={eager ? 'eager' : 'lazy'}
        fetchPriority={eager ? 'high' : undefined}
        objectFit={objectFit}
        h="100%"
        w="100%"
        onError={handleError}
      />
    </Box>
  );
};

export default BlurUpImage;
